// ============================================================================
// Notification bell — unread badge + dropdown list (polls every 60s)
// ============================================================================
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { notificationService, AppNotification } from '../services/notification.service';

const Bell = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round">
    <path d="M6 8a6 6 0 0112 0c0 7 3 9 3 9H3s3-2 3-9" /><path d="M10.3 21a1.94 1.94 0 003.4 0" />
  </svg>
);
const ago = (s: string) => {
  const d = (Date.now() - new Date(s).getTime()) / 1000;
  if (d < 60) return 'just now';
  if (d < 3600) return `${Math.floor(d / 60)}m ago`;
  if (d < 86400) return `${Math.floor(d / 3600)}h ago`;
  return `${Math.floor(d / 86400)}d ago`;
};

export const NotificationBell: React.FC = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [count, setCount] = useState(0);
  const [items, setItems] = useState<AppNotification[]>([]);
  const ref = useRef<HTMLDivElement>(null);

  const loadCount = useCallback(async () => {
    const r = await notificationService.unreadCount();
    if (r.success && r.data) setCount(r.data.count);
  }, []);
  const loadList = useCallback(async () => {
    const r = await notificationService.list();
    if (r.success && r.data) setItems(r.data);
  }, []);

  useEffect(() => { loadCount(); const t = setInterval(loadCount, 60000); return () => clearInterval(t); }, [loadCount]);
  useEffect(() => {
    const h = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', h); return () => document.removeEventListener('mousedown', h);
  }, []);

  const toggle = () => { const n = !open; setOpen(n); if (n) loadList(); };
  const onItem = async (it: AppNotification) => {
    if (!it.isRead) { await notificationService.markRead(it.notificationId); loadCount(); }
    setOpen(false);
    if (it.linkUrl) navigate(it.linkUrl);
  };
  const markAll = async () => { await notificationService.markAllRead(); setCount(0); loadList(); };

  return (
    <div className="relative" ref={ref}>
      <button onClick={toggle} className="relative p-2 rounded-xl text-slate-400 dark:text-[#9aa0a6] hover:bg-slate-100 dark:hover:bg-[#3c3c3c] hover:text-slate-600 dark:hover:text-[#e8eaed] transition-colors">
        <Bell />
        {count > 0 && <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 bg-guma-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">{count > 9 ? '9+' : count}</span>}
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-80 bg-white dark:bg-dark-surface rounded-xl shadow-lg border border-slate-200 dark:border-[#3c4043] z-30 overflow-hidden">
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-100 dark:border-[#3c4043]">
            <span className="text-sm font-semibold text-slate-900 dark:text-white">Notifications</span>
            {count > 0 && <button onClick={markAll} className="text-xs text-purple-600 hover:underline">Mark all read</button>}
          </div>
          <div className="max-h-96 overflow-y-auto">
            {!items.length ? <div className="px-4 py-8 text-center text-sm text-slate-400">No notifications.</div> : items.map(it => (
              <button key={it.notificationId} onClick={() => onItem(it)} className={`w-full text-left px-4 py-3 border-b border-slate-50 dark:border-[#2c2c2c] hover:bg-slate-50 dark:hover:bg-[#2c2c2c] ${it.isRead ? '' : 'bg-purple-50/40'}`}>
                <div className="flex items-start gap-2">
                  {!it.isRead && <span className="mt-1.5 w-2 h-2 rounded-full bg-purple-500 shrink-0" />}
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-slate-900 dark:text-white truncate">{it.title}</div>
                    {it.body && <div className="text-xs text-slate-500 dark:text-[#9aa0a6] line-clamp-2">{it.body}</div>}
                    <div className="text-[11px] text-slate-400 mt-0.5">{ago(it.createdAt)}</div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
